import type { CellValue } from "./xlsxReader";
import { readXlsxSheets, XlsxReadError } from "./xlsxReader";

/**
 * BOF-76 — the charge-point history kept by hand in the reference workbook, read for import.
 *
 * The workbook's `monthly-data` sheet has one row per month under the header
 * `Year | Month | Period | Active | Not Active | Total | …`. "Active" is the online count. Columns are
 * found by their header rather than by position, so a column inserted in the sheet does not silently
 * shift the figures.
 *
 * Rows the workbook has not filled in yet (future months carry a total of 0 and no active count) are
 * dropped, and every row that cannot be read is reported with its reason rather than guessed at.
 */

export const HISTORY_SHEET = "monthly-data";

export interface HistoryMonth {
  /** `YYYY-MM`. */
  month: string;
  total: number;
  online: number;
}

export interface HistoryParseResult {
  months: HistoryMonth[];
  /** Rows that look like data but could not be used, with the reason — shown, never silently dropped. */
  problems: string[];
}

/** One month of the workbook's `KPI_YYYY` sheets: "invoiced Clients" and "Service Fee" (converted to cents). */
export interface KpiHistoryMonth {
  /** `YYYY-MM`. */
  month: string;
  invoicedClients: number | null;
  serviceFeeCents: number | null;
}

/** Everything the workbook upload imports. */
export interface WorkbookHistory extends HistoryParseResult {
  /** From the `KPI_YYYY` sheets; empty for a `.csv` of `monthly-data`. */
  kpi: KpiHistoryMonth[];
}

const HEADERS = { year: "year", month: "month", online: "active", total: "total" } as const;

const toNumber = (v: CellValue | undefined): number | null => {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

const normalise = (v: CellValue | undefined) => String(v ?? "").trim().toLowerCase();

/** Finds the header row and the column of each field. */
const locateHeader = (rows: CellValue[][]) => {
  for (let r = 0; r < Math.min(rows.length, 20); r++) {
    const cells = (rows[r] ?? []).map(normalise);
    const col = (name: string) => cells.indexOf(name);
    const year = col(HEADERS.year);
    const month = col(HEADERS.month);
    const online = col(HEADERS.online);
    const total = col(HEADERS.total);
    if (year >= 0 && month >= 0 && online >= 0 && total >= 0) return { row: r, year, month, online, total };
  }
  return null;
};

/** Monthly figures from the sheet's rows, whether they came from an `.xlsx` or a `.csv`. */
export const parseHistoryRows = (rows: CellValue[][]): HistoryParseResult => {
  const header = locateHeader(rows);
  if (!header) {
    return {
      months: [],
      problems: ['No header row with "Year", "Month", "Active" and "Total" was found. Is this the monthly-data sheet?'],
    };
  }
  const months: HistoryMonth[] = [];
  const problems: string[] = [];
  const seen = new Set<string>();

  for (let r = header.row + 1; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const year = toNumber(row[header.year]);
    const month = toNumber(row[header.month]);
    if (year == null || month == null) continue; // blank or trailing rows
    const total = toNumber(row[header.total]);
    const online = toNumber(row[header.online]);
    // Months the workbook has not reached yet: no active count, total 0.
    if (online == null && (total == null || total === 0)) continue;

    const line = `Row ${r + 1}`;
    if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
      problems.push(`${line}: "${year}-${month}" is not a valid year and month.`);
      continue;
    }
    const key = `${year}-${String(month).padStart(2, "0")}`;
    if (total == null || online == null) {
      problems.push(`${line} (${key}): the total or the active count is missing.`);
      continue;
    }
    if (online > total || online < 0) {
      problems.push(`${line} (${key}): ${online} active is more than the ${total} total.`);
      continue;
    }
    if (seen.has(key)) {
      problems.push(`${line} (${key}): the month appears twice; the first row is used.`);
      continue;
    }
    seen.add(key);
    months.push({ month: key, total: Math.round(total), online: Math.round(online) });
  }
  months.sort((a, b) => a.month.localeCompare(b.month));
  return { months, problems };
};

/** A small RFC 4180 reader: quoted fields, escaped quotes, commas or semicolons. */
export const parseCsv = (text: string): string[][] => {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
  const delimiter = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ";" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delimiter) {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else field += ch;
  }
  if (field !== "" || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows;
};

const MONTH_ABBREVIATIONS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const KPI_SHEET = /^KPI_(\d{4})$/;

/**
 * The workbook's `KPI_YYYY` sheets: one row per month (`Jan`, `Feb`, …) under a header with "Service Fee"
 * and "invoiced Clients". A `KPI_2026` sheet opens with the previous December as its baseline row, so a
 * `Dec` that a `Jan` follows in the same sheet belongs to the year before. The same month in two sheets keeps the
 * figure from its own year's sheet. The fee is in euros in the sheet and in cents here.
 */
export const parseKpiSheets = (sheets: Record<string, CellValue[][]>): { months: KpiHistoryMonth[]; problems: string[] } => {
  const byMonth = new Map<string, { row: KpiHistoryMonth; ownYear: boolean }>();
  const problems: string[] = [];
  for (const [name, rows] of Object.entries(sheets)) {
    const year = Number(KPI_SHEET.exec(name)?.[1]);
    if (!year) continue;
    let headerRow = -1;
    let feeCol = -1;
    let clientsCol = -1;
    for (let r = 0; r < Math.min(rows.length, 5) && headerRow < 0; r++) {
      const cells = (rows[r] ?? []).map(normalise);
      feeCol = cells.findIndex((c) => c === "service fee");
      clientsCol = cells.findIndex((c) => c === "invoiced clients");
      if (feeCol >= 0 && clientsCol >= 0) headerRow = r;
    }
    if (headerRow < 0) {
      problems.push(`${name}: no header with "Service Fee" and "invoiced Clients" was found.`);
      continue;
    }
    const monthOf = (r: number) => MONTH_ABBREVIATIONS.indexOf(normalise((rows[r] ?? [])[0]).slice(0, 3));
    let firstJanuary = -1;
    for (let r = headerRow + 1; r < rows.length && firstJanuary < 0; r++) if (monthOf(r) === 0) firstJanuary = r;
    for (let r = headerRow + 1; r < rows.length; r++) {
      const row = rows[r] ?? [];
      const monthIndex = monthOf(r);
      if (monthIndex < 0) continue;
      const rowYear = monthIndex === 11 && firstJanuary > r ? year - 1 : year;
      const fee = toNumber(row[feeCol]);
      const clients = toNumber(row[clientsCol]);
      if (fee == null && clients == null) continue; // months the workbook has not reached
      const key = `${rowYear}-${String(monthIndex + 1).padStart(2, "0")}`;
      if ((fee ?? 0) < 0 || (clients ?? 0) < 0) {
        problems.push(`${name} ${key}: figures must not be negative.`);
        continue;
      }
      const candidate = {
        row: { month: key, invoicedClients: clients == null ? null : Math.round(clients), serviceFeeCents: fee == null ? null : Math.round(fee * 100) },
        ownYear: rowYear === year,
      };
      const existing = byMonth.get(key);
      if (!existing || (candidate.ownYear && !existing.ownYear)) byMonth.set(key, candidate);
    }
  }
  return { months: Array.from(byMonth.values(), (v) => v.row).sort((a, b) => a.month.localeCompare(b.month)), problems };
};

/**
 * Reads an uploaded workbook — `monthly-data` for the charge-point history and the `KPI_YYYY` sheets for
 * clients and the service fee — or the `monthly-data` sheet exported as `.csv`. Never throws: an
 * unreadable file comes back as a problem to show.
 */
export const readHistoryFile = async (file: File): Promise<WorkbookHistory> => {
  const name = file.name.toLowerCase();
  try {
    if (name.endsWith(".csv")) return { ...parseHistoryRows(parseCsv(await file.text())), kpi: [] };
    if (name.endsWith(".xlsx")) {
      const sheets = await readXlsxSheets(await file.arrayBuffer(), (names) =>
        names.filter((n) => n === HISTORY_SHEET || KPI_SHEET.test(n)),
      );
      if (!sheets[HISTORY_SHEET]) {
        return { months: [], kpi: [], problems: [`The workbook has no sheet named "${HISTORY_SHEET}".`] };
      }
      const charge = parseHistoryRows(sheets[HISTORY_SHEET]);
      const kpi = parseKpiSheets(sheets);
      return { months: charge.months, kpi: kpi.months, problems: [...charge.problems, ...kpi.problems] };
    }
    return { months: [], kpi: [], problems: ["Choose the workbook as .xlsx, or its monthly-data sheet as .csv."] };
  } catch (e) {
    const message = e instanceof XlsxReadError ? e.message : `The file could not be read: ${(e as Error)?.message ?? e}`;
    return { months: [], kpi: [], problems: [message] };
  }
};
